import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { TranslateParser, TranslateService } from '@ngx-translate/core';

import * as liftActions from '@app/lift/store/lifts.actions';
import {
    AddLiftSuccessAction, AddSlotSuccessAction,
    AddToolsToBatchSuccessAction, AddTraySuccessAction,
    CreateBatchWithToolsSuccessAction, DeleteBatchSuccessAction,
    DeleteLiftSuccessAction, RemoveToolsFromBatchSuccessAction,
    ToggleTrayStateSuccessAction, UPDATE_TRAY_SUCCESS, UpdateTraySuccessAction, CopySlotSuccessAction
} from '@app/lift/store/lifts.actions';
import { map } from 'rxjs/operators';
import { Tray } from '@models/tray.model';
import { environment } from '../../environments/environment';
import { ToastsManager } from "@app/shared/util/toastr.service";

@Injectable()
export class LiftNotificationsEffects {

    // Add lift success
    addLiftSuccess$ = createEffect(() => this.actions$.pipe(
        ofType(liftActions.ADD_LIFT_SUCCESS),
        map((data: AddLiftSuccessAction) => this.toastr.success(
            this.translateService.instant('admin.lifts.notifications.addLiftSuccess', {liftName: data.payload.name})
            , null, {toastLife: environment.toasts.duration}))
    ), {dispatch: false});

    // Rename lift success
    renameLiftSuccess$ = createEffect(() => this.actions$.pipe(
        ofType(liftActions.RENAME_LIFT_SUCCESS),
        map((data: AddLiftSuccessAction) => this.toastr.success(
            this.translateService.instant('admin.lifts.notifications.renameLiftSuccess', {liftName: data.payload.name})
            , null, {toastLife: environment.toasts.duration}))), {dispatch: false});

    // Delete lift
    deleteLiftSuccess$ = createEffect(() => this.actions$.pipe(
        ofType(liftActions.DELETE_LIFT_SUCCESS),
        map((data: DeleteLiftSuccessAction) => this.toastr.success(
            this.translateService.instant('admin.lifts.notifications.deleteLiftSuccess',
                {liftName: data.payload.name}), null, {toastLife: environment.toasts.duration}
        ))
    ), {dispatch: false});

    // Add tray
    addTraySuccess$ = createEffect(() => this.actions$.pipe(
        ofType(liftActions.ADD_TRAY_SUCCESS),
        map((data: AddTraySuccessAction) => this.toastr.success(
            this.translateService.instant('admin.lifts.notifications.addTraySuccess', {trayName: data.payload.name}),
            null, {toastLife: environment.toasts.duration}
        ))
    ), {dispatch: false});

    // Delete tray
    deleteTraySuccess$ = createEffect(() => this.actions$.pipe(
        ofType(liftActions.DELETE_TRAY_SUCCESS),
        map((data: AddTraySuccessAction) => this.toastr.success(
            this.translateService.instant('admin.lifts.notifications.deleteTraySuccess', {trayName: data.payload.name}),
            null, {toastLife: environment.toasts.duration}
        ))
    ), {dispatch: false});

    // Toggle tray active
    toggleTrayActiveSuccess$ = createEffect(() => this.actions$.pipe(
        ofType(liftActions.TOGGLE_TRAY_STATE_SUCCESS),
        map((toggleAction: ToggleTrayStateSuccessAction) => this.toastr.success(
                this.translateService.instant('admin.lifts.notifications.toggleTrayStateSuccess',
                    {
                        id: toggleAction.payload.id,
                        state: this.computeActiveState(toggleAction.payload)
                    }), null, {toastLife: environment.toasts.duration}
            )
        )), {dispatch: false});

    // Toggle tray active
    updateTraySuccess$ = createEffect(() => this.actions$.pipe(
        ofType(liftActions.UPDATE_TRAY_SUCCESS),
        map((updateAction: UpdateTraySuccessAction) => this.toastr.success(
                this.translateService.instant('admin.lifts.notifications.updateTrayStateSuccess'),
                null, {toastLife: environment.toasts.duration}
            )
        )), {dispatch: false});

    // Add Slot
    addSlotSuccess$ = createEffect(() => this.actions$.pipe(
        ofType(liftActions.ADD_SLOT_SUCCESS),
        map((addSlot: AddSlotSuccessAction) => this.toastr.success(
                this.translateService.instant('admin.lifts.notifications.addNewSlot'), null,
                {toastLife: environment.toasts.duration}
            )
        )), {dispatch: false});

    // Copy Slot
    copySlotSuccess$ = createEffect(() => this.actions$.pipe(
        ofType(liftActions.COPY_SLOT_SUCCESS),
        map((copySlot: CopySlotSuccessAction) => this.toastr.success(
                this.translateService.instant('admin.lifts.notifications.copySlotSuccess'), null,
                {toastLife: environment.toasts.duration}
            )
        )), {dispatch: false});

    // Delete Slot
    deleteSlotSuccess$ = createEffect(() => this.actions$.pipe(
        ofType(liftActions.DELETE_SLOT_SUCCESS),
        map(() => this.toastr.success(
            this.translateService.instant('admin.lifts.notifications.deleteSlot'), null, {toastLife: environment.toasts.duration}
        ))
    ), {dispatch: false});

    // Update Slot
    updateSlotSuccess$ = createEffect(() => this.actions$.pipe(
        ofType(liftActions.RENAME_SLOT_SUCCESS),
        map(() => this.toastr.success(
            this.translateService.instant('admin.lifts.notifications.updateSlot'), null, {toastLife: environment.toasts.duration}
        ))
    ), {dispatch: false});

    // Add Tools to Batch
    addToolsToBatch$ = createEffect(() => this.actions$.pipe(
        ofType(liftActions.ADD_TOOLS_TO_BATCH_SUCCESS),
        map((batch: AddToolsToBatchSuccessAction) => this.toastr.success(
            this.translateService.instant('admin.lifts.notifications.toolsAddedToBatch', {batchName: batch.payload.name}),
            null, {toastLife: environment.toasts.duration}
        ))
    ), {dispatch: false});

    // Add Tools to New Batch
    addToolsToNewBatch$ = createEffect(() => this.actions$.pipe(
        ofType(liftActions.CREATE_BATCH_WITH_TOOLS_SUCCESS),
        map((batch: CreateBatchWithToolsSuccessAction) => this.toastr.success(
            this.translateService.instant('admin.lifts.notifications.toolsAddedToNewBatch', {batchName: batch.payload.name}),
            null, {toastLife: environment.toasts.duration}
        ))
    ), {dispatch: false});

    // Remove Tools From Batch
    removeToolsFromBatchSuccess$ = createEffect(() => this.actions$.pipe(
        ofType(liftActions.REMOVE_TOOLS_FROM_BATCH_SUCCESS),
        map((batch: RemoveToolsFromBatchSuccessAction) => this.toastr.success(
            this.translateService.instant('admin.lifts.notifications.toolsRemovedFromBatch', {batchName: batch.payload.name}),
            null, {toastLife: environment.toasts.duration}
        ))
    ), {dispatch: false});

    // Delete Batch Success
    removeBatchSuccess$ = createEffect(() => this.actions$.pipe(
        ofType(liftActions.DELETE_BATCH_SUCCESS),
        map((batch: DeleteBatchSuccessAction) => this.toastr.success(
            this.translateService.instant('admin.lifts.notifications.deleteBatchSuccess', {batchName: batch.payload.name}),
            null, {toastLife: environment.toasts.duration}
        ))
    ), {dispatch: false});

    // Update Cart Batch Tools
    updateCartToolsFromBatchSuccess$ = createEffect(() => this.actions$.pipe(
        ofType(liftActions.UPDATE_CART_BATCH_TOOLS_SUCCESS),
        map((batch: RemoveToolsFromBatchSuccessAction) => this.toastr.success(
            this.translateService.instant('admin.lifts.notifications.updateCartBatchTools'),
            null, {toastLife: environment.toasts.duration}
        ))
    ), {dispatch: false});

    constructor(
        private translateService: TranslateService,
        private toastr: ToastsManager,
        private translateParser: TranslateParser,
        private actions$: Actions
    ) {

    }

    private computeActiveState(tray: Tray) {
        if (tray && !!tray.disabled) {
            return this.translateService.instant('Suspended');
        } else {
            return this.translateService.instant('Active')
        }
    }
}
